import { SearchableSelect } from "@/components/ui/searchable-select";
import React, { useCallback, useEffect, useState } from "react";
import { TimeInput } from "@/components/ui/time-input";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { PageHeader, StatusTabs } from "@/components/patterns";
import { useSearchParams } from "react-router-dom";
import { IncomingCallsPanel } from "./IncomingCallsPage";
import { ErrorState } from "@/components/patterns/States";
import { useSoftphone } from "@/components/calling/SoftphoneContext";
import { AlertTriangle, CheckCircle2, Copy, Loader2, Phone, PhoneCall, PlugZap, Save, Clock, Mic, Users } from "lucide-react";
import { dateLocale, withClock } from "@/lib/dateTime";

type Hours = { enabled: boolean; start: string; end: string; days: number[]; timezone: string };
type Account = {
  auth_id: string;
  has_auth_token: boolean;
  app_id: string;
  caller_id: string;
  record_calls: boolean;
  recording_retention_days: number;
  calling_hours: Hours;
  connected_min_sec: number;
  rule_mandatory_outcome: boolean;
  rule_do_not_call: boolean;
  rule_min_gap_minutes: number;
  rule_max_attempts_per_day: number;
  rule_daily_cap: number;
  hot_lead_alert_hours: number;
  plivo_account_name: string | null;
  connection_status: "CONNECTED" | "ERROR";
  last_error: string | null;
  last_checked_at: string | null;
  /** Set while the five-minute Plivo check is failing; the Super Admins are told after two failed checks */
  down_since?: string | null;
  failed_checks?: number;
};
type Rep = { id: string; name: string; email: string; role: string; enabled: boolean };
type Settings = {
  connected: boolean;
  webhooks: { answer_url: string; hangup_url: string; recording_url: string };
  account: Account | null;
  defaults: { calling_hours: Hours; recording_retention_days: number };
  warnings?: string[];
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const ZONES = ["Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Europe/London", "America/New_York", "America/Los_Angeles", "Australia/Sydney"];
const fmtWhen = (iso?: string | null) => (iso ? new Date(iso).toLocaleString(dateLocale(), withClock({ day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })) : "never");
/** "918045678900" → "+91 8045678900" is guesswork, so show the digits the way they were entered when possible */
const showNumber = (digits: string) => (digits ? `+${digits}` : "");

export function CallingSettingsPage() {
  const { success, error, warning } = useToast();
  const [data, setData] = useState<Settings | null>(null);
  const [problem, setProblem] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [reps, setReps] = useState<Rep[]>([]);
  const [repBusy, setRepBusy] = useState<string | null>(null);
  const [myMobile, setMyMobile] = useState("");
  const [mobileBusy, setMobileBusy] = useState(false);
  const sp = useSoftphone();
  const [params, setParams] = useSearchParams();
  const tab = ["connection", "people", "hours", "recording", "incoming", "ringtone"].includes(params.get("tab") || "") ? (params.get("tab") as string) : "connection";
  const setTab = (t: string) => { const n = new URLSearchParams(params); n.set("tab", t); setParams(n, { replace: true }); };

  const [authId, setAuthId] = useState("");
  const [token, setToken] = useState("");
  const [callerId, setCallerId] = useState("");
  const [appId, setAppId] = useState("");
  const [record, setRecord] = useState(false);
  const [retention, setRetention] = useState(180);
  const [hours, setHours] = useState<Hours | null>(null);
  const [rules, setRules] = useState({ connected_min_sec: 10, rule_mandatory_outcome: false, rule_do_not_call: false, rule_min_gap_minutes: 0, rule_max_attempts_per_day: 0, rule_daily_cap: 0, hot_lead_alert_hours: 0 });

  const fill = useCallback((s: Settings) => {
    setData(s);
    const a = s.account;
    setAuthId(a?.auth_id || "");
    setToken("");
    setCallerId(showNumber(a?.caller_id || ""));
    setAppId(a?.app_id || "");
    setRecord(a?.record_calls ?? false);
    setRetention(a?.recording_retention_days ?? s.defaults.recording_retention_days);
    setHours(a?.calling_hours ?? s.defaults.calling_hours);
    setRules({
      connected_min_sec: a?.connected_min_sec ?? 10,
      rule_mandatory_outcome: a?.rule_mandatory_outcome ?? false,
      rule_do_not_call: a?.rule_do_not_call ?? false,
      rule_min_gap_minutes: a?.rule_min_gap_minutes ?? 0,
      rule_max_attempts_per_day: a?.rule_max_attempts_per_day ?? 0,
      rule_daily_cap: a?.rule_daily_cap ?? 0,
      hot_lead_alert_hours: a?.hot_lead_alert_hours ?? 0,
    });
  }, []);

  const load = useCallback(async () => {
    try {
      fill(unwrapObject<Settings>(await api.get("/api/Call/Settings")));
      setProblem(null);
    } catch (err) {
      setProblem(err);
    }
  }, [fill]);

  const loadReps = useCallback(async () => {
    try {
      setReps(unwrapObject<Rep[]>(await api.get("/api/Call/Reps")) || []);
    } catch {
      setReps([]);
    }
  }, []);

  useEffect(() => { load(); loadReps(); }, [load, loadReps]);

  const saveMyMobile = async () => {
    if (!myMobile.trim()) return;
    setMobileBusy(true);
    try {
      await sp.saveMobile(myMobile.trim());
      setMyMobile("");
      success("Mobile number saved", "The test call will ring it");
    } catch (err: any) {
      error("Couldn't save the number", err.message || "Check it and try again");
    } finally {
      setMobileBusy(false);
    }
  };

  const toggleRep = async (rep: Rep, on: boolean) => {
    setRepBusy(rep.id);
    try {
      setReps(unwrapObject<Rep[]>(await api.post(`/api/Call/Reps/${rep.id}/${on ? "Enable" : "Disable"}`)) || []);
      success(on ? "Calling switched on" : "Calling switched off", rep.name);
    } catch (err: any) {
      error("Couldn't change calling", err.message || "Please try again");
    } finally {
      setRepBusy(null);
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data || !hours) return;
    if (!authId.trim() || !callerId.trim() || (!token.trim() && !data.account?.has_auth_token)) {
      error("Missing details", "Enter the Account ID, Secret code and the calling number");
      return;
    }
    setSaving(true);
    try {
      const r = unwrapObject<Settings>(
        await api.post("/api/Call/Settings", {
          auth_id: authId.trim(),
          ...(token.trim() ? { auth_token: token.trim() } : {}),
          ...(appId.trim() ? { app_id: appId.trim() } : {}),
          caller_id: callerId.trim(),
          record_calls: record,
          recording_retention_days: Number(retention) || 0,
          calling_hours: hours,
          ...rules,
        }),
      );
      fill(r);
      setWarnings(r.warnings || []);
      success("Calling settings saved", "Your calling account accepted the details");
    } catch (err: any) {
      error("Couldn't save", err.message || "Please check the details and try again");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = unwrapObject<{ ok: boolean; account_name?: string; balance?: string; error?: string; warnings?: string[] }>(await api.post("/api/Call/TestConnection"));
      if (r.ok) {
        success("Calling account connected", `${r.account_name}${r.balance ? ` · balance ${r.balance}` : ""}`);
        setWarnings(r.warnings || []);
      } else {
        error("Couldn't connect the calling account", r.error || "Check the details and try again");
      }
      await load();
    } catch (err: any) {
      error("Couldn't test", err.message || "Please try again");
    } finally {
      setTesting(false);
    }
  };

  const createApplication = async () => {
    setCreating(true);
    try {
      fill(unwrapObject<Settings>(await api.post("/api/Call/CreateApplication")));
      setWarnings([]);
      success("Call setup ready", "Calls will now update this CRM");
    } catch (err: any) {
      error("Couldn't finish the call setup", err.message || "Please try again");
    } finally {
      setCreating(false);
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      success("Copied");
    } catch {
      warning("Couldn't copy", "Select the text and copy it by hand");
    }
  };

  if (!data && !problem) return <FormSkeleton />;
  if (problem || !data || !hours) return <ErrorState title="Couldn't load calling settings" body="Try again in a moment." onRetry={load} error={problem} />;

  const acc = data.account;
  const toggleDay = (d: number) => setHours({ ...hours, days: hours.days.includes(d) ? hours.days.filter((x) => x !== d) : [...hours.days, d].sort() });

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <PageHeader
        title="Calling"
        summary={acc ? (acc.connection_status === "CONNECTED" ? "Calling account connected" : "Calling account has a problem") : "Not set up"}
        actions={
          acc && (
            <Button variant="outline" size="sm" onClick={test} disabled={testing}>
              {testing ? <Loader2 className="size-4 animate-spin" /> : <PlugZap className="size-4" />} Test connection
            </Button>
          )
        }
      />

      {acc?.connection_status === "ERROR" && (
        <div className="rounded-lg border border-destructive/30 bg-danger-soft px-4 py-3 text-sm text-destructive flex gap-2">
          <AlertTriangle className="size-4 mt-0.5 shrink-0" />
          <div>Your calling account isn't accepting the saved details{acc.last_error ? `: ${acc.last_error}` : "."} Calls will fail until this is fixed.{acc.down_since ? ` Failing since ${fmtWhen(acc.down_since)}; the Super Admins have been told.` : ""}</div>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="rounded-lg border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning space-y-1">
          {warnings.map((w) => (
            <div key={w} className="flex gap-2"><AlertTriangle className="size-4 mt-0.5 shrink-0" /><span>{w}</span></div>
          ))}
        </div>
      )}

      <StatusTabs
        label="Calling settings"
        active={tab}
        onChange={setTab}
        tabs={[{ key: "connection", label: "Connection" }, { key: "people", label: "People" }, { key: "hours", label: "Hours and rules" }, { key: "recording", label: "Recording" }, { key: "incoming", label: "Incoming" }, { key: "ringtone", label: "Ringtone" }]}
      />

      {tab === "incoming" && <IncomingCallsPanel onOpenHours={() => setTab("hours")} />}
      {tab === "ringtone" && <IncomingCallsPanel section="ringtone" />}

      <form onSubmit={save} className="space-y-6" hidden={tab === "incoming" || tab === "ringtone"}>
        <div hidden={tab !== "connection"} className="space-y-6">
        <Card className="shadow-xs">
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Phone className="size-4" /></div>
              <div>
                <CardTitle className="text-base font-semibold">Calling account</CardTitle>
                <CardDescription>
                  {acc ? <>Account <b>{acc.plivo_account_name || "—"}</b> · last checked {fmtWhen(acc.last_checked_at)}</> : "Reps call leads from the CRM through your calling account. You'll find these details on your calling provider's dashboard."}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="call-auth-id">Account ID</Label>
              <Input id="call-auth-id" value={authId} onChange={(e) => setAuthId(e.target.value)} className="font-mono" autoComplete="off" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="call-auth-token">Secret code</Label>
              <Input id="call-auth-token" type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder={acc?.has_auth_token ? "Saved. Leave empty to keep it" : ""} className="font-mono" autoComplete="new-password" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="call-caller-id">Calling number (the company number leads see)</Label>
              <Input id="call-caller-id" value={callerId} onChange={(e) => setCallerId(e.target.value)} placeholder="+91 80 4567 8900" className="font-mono" autoComplete="off" />
              <p className="text-xs text-muted-foreground">A number rented on your calling account, with its country code.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="call-app-id">Call setup</Label>
              <div className="flex gap-2">
                <Input id="call-app-id" value={appId} onChange={(e) => setAppId(e.target.value)} placeholder="None yet" className="font-mono" autoComplete="off" />
                <Button type="button" variant="outline" onClick={createApplication} disabled={creating || !acc}>
                  {creating ? <Loader2 className="size-4 animate-spin" /> : null} {acc?.app_id ? "Repoint" : "Create"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">{acc ? "Calls update this CRM through this setup." : "Save the details first, then create the call setup."}</p>
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Links your calling account uses</Label>
              {(["answer_url", "hangup_url", "recording_url"] as const).map((k) => (
                <div key={k} className="flex items-center gap-2 rounded-md bg-muted px-3 py-1.5">
                  <span className="text-xs text-muted-foreground w-16 shrink-0">{k === "answer_url" ? "Answer" : k === "hangup_url" ? "Hang-up" : "Recording"}</span>
                  <span className="font-mono text-[12.5px] truncate flex-1">{data.webhooks[k]}</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => copy(data.webhooks[k])} aria-label={`Copy the ${k.replace("_url", "")} link`}><Copy className="size-4" /></Button>
                </div>
              ))}
              <p className="text-xs text-muted-foreground">"Create" fills these in for you. Copy them by hand only if you manage the call setup on your calling provider's dashboard yourself.</p>
            </div>
          </CardContent>
        </Card>
        </div>

        <div hidden={tab !== "people"} className="space-y-6">
        <Card className="shadow-xs">
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Users className="size-4" /></div>
              <div>
                <CardTitle className="text-base font-semibold">Who can call</CardTitle>
                <CardDescription>
                  Switching a person on gives them a calling login, created for them. There is no password to share. Turn it off to remove it.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {!acc?.app_id ? (
              <p className="text-sm text-muted-foreground">Save the calling account details and create the call setup first. Then you can switch people on.</p>
            ) : reps.length === 0 ? (
              <p className="text-sm text-muted-foreground">No users found.</p>
            ) : (
              <ul className="divide-y rounded-lg border">
                {reps.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{r.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{r.role}{r.role ? " · " : ""}{r.email}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {repBusy === r.id && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
                      <Switch checked={r.enabled} disabled={repBusy !== null} onCheckedChange={(v) => toggleRep(r, v)} aria-label={`Calling for ${r.name}`} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {acc?.app_id && (
              <div className="flex flex-wrap items-center gap-3 rounded-lg bg-muted px-3 py-2.5 text-sm">
                <PhoneCall className="size-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-[200px]">
                  <div className="font-medium">Try it yourself</div>
                  <div className="text-xs text-muted-foreground">
                    {sp.phase === "off" ? "Switch yourself on above, then reload this page. Your own test call is on Settings → My calling." : sp.phase === "ready" ? (sp.hasMobile ? "Rings your own mobile through the headset, to check the whole path." : "Add your mobile number below first. The test call rings it.") : sp.phase === "offline" ? "Calling in the browser couldn't sign in. Settings → My calling shows why." : sp.phase === "connecting" ? "Calling in the browser is signing in…" : "A call is in progress."}
                  </div>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={sp.startTestCall} disabled={!sp.canTestCall}>Test call to my mobile</Button>
                {sp.phase !== "off" && (
                  <div className="basis-full flex flex-wrap items-end gap-2 border-t pt-2.5">
                    <div className="space-y-1 flex-1 min-w-[220px] max-w-sm">
                      <Label htmlFor="call-my-mobile" className="text-xs">{sp.hasMobile ? "Change your mobile number" : "Your mobile number"}</Label>
                      <Input id="call-my-mobile" type="tel" value={myMobile} onChange={(e) => setMyMobile(e.target.value)} placeholder="+91 98765 43210" autoComplete="tel" />
                    </div>
                    <Button type="button" size="sm" variant="outline" onClick={saveMyMobile} disabled={mobileBusy || !myMobile.trim()}>
                      {mobileBusy ? <Loader2 className="size-4 animate-spin" /> : null} Save number
                    </Button>
                    {sp.hasMobile && <span className="text-xs text-success inline-flex items-center gap-1 pb-2"><CheckCircle2 className="size-3.5" /> A number is saved</span>}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        </div>

        <div hidden={tab !== "hours"} className="space-y-6">
        <Card className="shadow-xs">
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Clock className="size-4" /></div>
              <div>
                <CardTitle className="text-base font-semibold">Calling hours</CardTitle>
                <CardDescription>Reps can't start a call outside these hours, and customers who call outside them hear your closed message and can leave a voicemail. The same hours apply to outgoing and incoming calls.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5">
              <div className="text-sm"><div className="font-medium">Limit calling hours</div><div className="text-xs text-muted-foreground">Switch off to allow calls at any time</div></div>
              <Switch checked={hours.enabled} onCheckedChange={(v) => setHours({ ...hours, enabled: v })} aria-label="Limit calling hours" />
            </div>
            <div className={`grid gap-4 sm:grid-cols-3 ${hours.enabled ? "" : "opacity-50 pointer-events-none"}`}>
              <div className="space-y-1.5"><Label htmlFor="call-start">From</Label><TimeInput id="call-start" label="Calling hours from" value={hours.start} onChange={(v) => setHours({ ...hours, start: v })} /></div>
              <div className="space-y-1.5"><Label htmlFor="call-end">Until</Label><TimeInput id="call-end" label="Calling hours until" value={hours.end} onChange={(v) => setHours({ ...hours, end: v })} /></div>
              <div className="space-y-1.5">
                <Label htmlFor="call-zone">Time zone</Label>
                <SearchableSelect id="call-zone" value={hours.timezone} onChange={(e) => setHours({ ...hours, timezone: e.target.value })} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                  {[...new Set([hours.timezone, ...ZONES])].map((z) => <option key={z} value={z}>{z.replace("_", " ")}</option>)}
                </SearchableSelect>
              </div>
              <div className="sm:col-span-3 flex flex-wrap gap-2" role="group" aria-label="Days calls are allowed">
                {DAYS.map((d, i) => (
                  <button key={d} type="button" aria-pressed={hours.days.includes(i)} onClick={() => toggleDay(i)}
                    className={`h-8 px-3 rounded-md text-sm border transition-colors ${hours.days.includes(i) ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground hover:text-foreground"}`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
        </div>

        <div hidden={tab !== "recording"} className="space-y-6">
        <Card className="shadow-xs">
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Mic className="size-4" /></div>
              <div>
                <CardTitle className="text-base font-semibold">Recording</CardTitle>
                <CardDescription>Recordings are saved to your private storage and can be played from the lead's timeline by people allowed to hear them.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5">
              <div className="text-sm"><div className="font-medium">Record calls</div><div className="text-xs text-muted-foreground">Leads hear "This call may be recorded" before they are connected</div></div>
              <Switch checked={record} onCheckedChange={setRecord} aria-label="Record calls" />
            </div>
            <div className="space-y-1.5 max-w-xs">
              <Label htmlFor="call-retention">Keep recordings for (days)</Label>
              <Input id="call-retention" type="number" min={0} max={3650} value={retention} onChange={(e) => setRetention(Number(e.target.value))} />
              <p className="text-xs text-muted-foreground">Older recordings are deleted automatically. 0 keeps them forever.</p>
            </div>
          </CardContent>
        </Card>
        </div>

        <div hidden={tab !== "hours"} className="space-y-6">
        <Card className="shadow-xs">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Rules</CardTitle>
            <CardDescription>Checked before every call. A rep who is stopped is told why, in plain words. A number of 0 turns that rule off.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {([
              ["rule_mandatory_outcome", "Outcome before the next call", "A rep can't start a call until the last one has an outcome"],
              ["rule_do_not_call", "Respect \"asked not to call\"", "Leads and contacts marked do not call can't be dialled until a Sales Head allows it"],
            ] as const).map(([k, title, hint]) => (
              <div key={k} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5">
                <div className="text-sm"><div className="font-medium">{title}</div><div className="text-xs text-muted-foreground">{hint}</div></div>
                <Switch checked={rules[k]} onCheckedChange={(v) => setRules((r) => ({ ...r, [k]: v }))} aria-label={title} />
              </div>
            ))}
            <div className="grid gap-3 sm:grid-cols-2">
              {([
                ["rule_min_gap_minutes", "Minutes between calls to the same number", "Stops back-to-back redials", 1440],
                ["rule_max_attempts_per_day", "Calls to one number per day", "After this, the rep must wait until tomorrow", 50],
                ["rule_daily_cap", "Calls per rep per day", "Protects the company number's reputation", 2000],
                ["hot_lead_alert_hours", "Hot lead not called, alert after (hours)", "The rep and their Sales Head are told once", 168],
                ["connected_min_sec", "A call counts as connected after (seconds)", "Shorter answers are voicemail or wrong numbers in the reports", 120],
              ] as const).map(([k, label, hint, max]) => (
                <div key={k} className="space-y-1.5">
                  <Label htmlFor={`rule-${k}`}>{label}</Label>
                  <Input id={`rule-${k}`} type="number" min={k === "connected_min_sec" ? 1 : 0} max={max} value={rules[k]} onChange={(e) => setRules((r) => ({ ...r, [k]: Number(e.target.value) || 0 }))} />
                  <p className="text-xs text-muted-foreground">{hint}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        </div>

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={saving}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} Save and check the connection
          </Button>
          {acc?.connection_status === "CONNECTED" && <span className="text-sm text-success inline-flex items-center gap-1.5"><CheckCircle2 className="size-4" /> Connected</span>}
        </div>
      </form>
    </div>
  );
}
